/**
 * Design Studio home, My Designs gallery, and Canva import —
 * Learn-Mode inline tips.
 *
 * Covers `routes/social/DesignGallery.tsx`,
 * `routes/social/_sections/design-gallery/*`,
 * `routes/social/_sections/design-studio/*` (the Studio landing tab, not the
 * canvas editor — see `design-editor.ts` for that), and
 * `routes/social/_sections/canva-import/*`.
 */

import type { LearnTip } from "./types";

export const designStudioTips: LearnTip[] = [
  {
    key: "design-studio-home",
    title: "Design Studio",
    body: "Create a graphic from scratch, a template, or an AI brief, then post it in one click. New designs save to whichever client is selected.",
  },
  {
    key: "social-assets-filters",
    title: "Client Library filters",
    body: 'Narrow a large client library without loading all of it. Every filter runs on the server, so the "Showing N of TOTAL" count is always the real number of matches.',
  },
  {
    key: "social-assets-search",
    title: "Search assets",
    body: "Matches filenames, alt text, tags and the AI photo description. Results update a moment after you stop typing.",
  },
  {
    key: "social-assets-type",
    title: "Asset type",
    body: "Photos, Videos, Raw footage or Documents. Raw footage is everything filed straight from the camera or phone — original clips and photos, before editing.",
  },
  {
    key: "social-assets-collection",
    title: "Collection",
    body: "Jump to one folder of the library, such as Raw footage & photos or the Posted archive. Folders inside the one you pick are included.",
  },
  {
    key: "social-assets-posted",
    title: "Posted status",
    body: "Never posted shows media this client has not published yet — the best place to find fresh content. Posted shows what has already gone out.",
  },
  {
    key: "social-assets-preview",
    title: "Preview",
    body: "Play a video or view a photo full size, see its tags, and download the original file.",
  },
  {
    key: "studio-search",
    title: "Search",
    body: "Search formats, your own past designs, and the template library all at once from a single search box.",
  },
  {
    key: "studio-create-design",
    title: "Create a Design",
    body: "Pick a canvas size to start from, filtered by platform, or enter a custom size.",
  },
  {
    key: "studio-recent-designs",
    title: "Your Recent Designs",
    body: "The designs you've worked on most recently — click one to pick up right where you left off.",
  },
  {
    key: "studio-suggested-templates",
    title: "Suggested Templates",
    body: "Template picks based on what this client (or your team) designs most often, so you don't have to search for a good starting point.",
  },
  {
    key: "studio-brand-kit",
    title: "Brand Kit",
    body: "A quick look at the selected client's brand kit, so you can confirm colors and fonts are set up before you start designing.",
  },
  {
    key: "design-gallery",
    title: "My Designs",
    body: "Every design across your clients, organized in folders. Search, filter by client, and bulk-manage them from one place.",
  },
  {
    key: "design-gallery-update-brand",
    title: "Update to Latest Brand",
    body: "Re-applies this client's current brand kit (colors, fonts, logo) to their existing designs — your headlines and text stay exactly as written.",
  },
  {
    key: "design-gallery-filters",
    title: "Search & Filter Designs",
    body: "Search by design name or tag, and filter the gallery down to what you're looking for.",
  },
  {
    key: "design-gallery-bulk-actions",
    title: "Bulk Actions",
    body: "Appears once you select one or more designs — move them to a folder, delete them, or act on the whole selection at once.",
  },
  {
    key: "design-gallery-folders",
    title: "Folders",
    body: "Organize designs into folders by campaign, client, or however your team works. “Unfiled” shows designs not yet assigned to one.",
  },
  {
    key: "social-canva-connection",
    title: "Canva Connection",
    body: "Links your Canva account so you can import designs you've already built there. A yellow warning means the connection needs refreshing before an import will work.",
  },
  {
    key: "social-canva-export-progress",
    title: "Import Progress",
    body: "Tracks how many designs from your Canva export have finished importing, and flags any that failed so you can retry just those.",
  },
];
